import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Ambient background music — hidden YouTube IFrame Player + a small mute icon
 * in the navbar (rendered via portal-style inline since this is mounted inside
 * Navbar). Auto-starts MUTED (browsers block autoplay-with-sound) and remembers
 * the member's preference in localStorage.
 *
 * Source of truth: GET /api/music/ambient → returns the single track an admin
 * has marked is_ambient=true (or falls back to a featured Worship track).
 */
const PREF_KEY = "jj_ambient_pref"; // 'on' | 'off'

export default function AmbientPlayer() {
  const { user } = useAuth();
  const [track, setTrack] = useState(null);
  const [ready, setReady] = useState(false);
  const [unmuted, setUnmuted] = useState(false);
  const playerRef = useRef(null);
  const containerRef = useRef(null);

  // Pull the ambient track (only when logged in — the public landing has its
  // own embedded watch-party carousel and we don't want background noise there).
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/music/ambient")
      .then(({ data }) => { if (alive) setTrack(data); })
      .catch(() => { if (alive) setTrack(null); });
    return () => { alive = false; };
  }, [user]);

  // Load the YouTube IFrame API once.
  useEffect(() => {
    if (!track || playerRef.current) return;
    const onAPIReady = () => {
      try {
        // eslint-disable-next-line no-undef
        playerRef.current = new YT.Player(containerRef.current, {
          videoId: track.youtube_id,
          playerVars: {
            autoplay: 1,
            controls: 0,
            disablekb: 1,
            loop: 1,
            playlist: track.youtube_id, // required when loop=1
            modestbranding: 1,
            playsinline: 1,
            iv_load_policy: 3,
          },
          events: {
            onReady: (e) => {
              e.target.mute();
              e.target.playVideo();
              setReady(true);
              // If the member had previously turned music ON, honor that.
              if (localStorage.getItem(PREF_KEY) === "on") {
                e.target.unMute();
                setUnmuted(true);
              }
            },
            onStateChange: (e) => {
              // 0 = ended — loop manually in case loop param is unreliable on some embeds.
              // eslint-disable-next-line no-undef
              if (e.data === YT.PlayerState.ENDED) e.target.playVideo();
            },
          },
        });
      } catch {
        /* youtube blocked or iframe failed — silently skip */
      }
    };
    if (window.YT && window.YT.Player) {
      onAPIReady();
    } else {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      tag.async = true;
      document.head.appendChild(tag);
      window.onYouTubeIframeAPIReady = onAPIReady;
    }
  }, [track]);

  const toggle = () => {
    if (!ready || !playerRef.current) return;
    if (unmuted) {
      playerRef.current.mute();
      localStorage.setItem(PREF_KEY, "off");
      setUnmuted(false);
    } else {
      try { playerRef.current.unMute(); playerRef.current.playVideo(); } catch {}
      localStorage.setItem(PREF_KEY, "on");
      setUnmuted(true);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Hidden 1x1 iframe holder — kept offscreen so the audio plays but no video shows. */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          width: 1,
          height: 1,
          left: -9999,
          top: -9999,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div ref={containerRef} />
      </div>

      {/* Visible mute toggle — only if an ambient track exists. */}
      {track && (
        <button
          onClick={toggle}
          disabled={!ready}
          aria-label={unmuted ? "Mute ambient worship" : "Unmute ambient worship"}
          title={track ? `${unmuted ? "Mute" : "Play"} — ${track.title} by ${track.artist}` : "Ambient worship"}
          className={`relative inline-flex items-center justify-center h-9 w-9 rounded-full transition-colors ${unmuted ? "bg-burgundy text-cream" : "text-jjblue/80 hover:text-burgundy"}`}
          data-testid="ambient-toggle"
        >
          {unmuted ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          {!unmuted && ready && (
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-gold animate-pulse" aria-hidden="true" />
          )}
        </button>
      )}
    </>
  );
}
